import { sendAnalytics } from '@audio-network/analytics'
import { getCookie, setCookie } from 'cookies-next'
import throttle from 'lodash/throttle'

import { SpotlightPosition } from './Spotlight.types'

export const SPOTLIGHT_CONSTANTS = {
	COOKIE_NAME: 'spotlight',
	MIN_WIDTH_MEDIA_QUERY: '(min-width: 48em)',
} as const

/**
 * A very small subset of our users are using browsers that don't support
 * the Popover API. Because a Spotlight is not a core part of our
 * product, we'll just render nothing.
 *
 * We also don't support Spotlights on mobile devices.
 *
 * NOTE: We can't test this in a test environment because the window object
 * is not available (we would have to mock absolutely everything, which proves nothing :)).
 */
export const isSpotlightSupported = (): boolean =>
	typeof window !== 'undefined' &&
	'popover' in window.HTMLDivElement.prototype &&
	window.matchMedia(SPOTLIGHT_CONSTANTS.MIN_WIDTH_MEDIA_QUERY).matches

export const calculateSpotlightPosition = (
	target: Element,
	spotlight: Element,
	isAnimating: boolean
): SpotlightPosition => {
	const { x, y, height } = target.getBoundingClientRect()
	const { width: panelWidth } = spotlight.getBoundingClientRect()
	// Account for the initial scale of 0.8 during the animation
	const scaledWidth = isAnimating ? panelWidth / 0.8 : panelWidth
	return {
		top: y + window.scrollY - height / 2,
		left: x - (scaledWidth + 16),
		isVisible: x !== 0 && y !== 0 && height !== 0,
	}
}

export const positionSpotlight = (
	describes: string,
	spotlightRef: React.RefObject<HTMLDivElement>,
	isAnimating: boolean
): void => {
	if (!spotlightRef.current || !describes) return

	const spotlight = spotlightRef.current
	const target = document.querySelector(describes)
	if (!spotlight || !target) return

	const { top, left, isVisible } = calculateSpotlightPosition(
		target,
		spotlight,
		isAnimating
	)

	if (isVisible) {
		spotlight.setAttribute(
			'style',
			`visibility: visible; top: ${top}px; left: ${left}px;`
		)
	} else {
		spotlight.hidePopover()
	}
}

export const setupPositionTracking = (
	onPositionUpdate: () => void
): (() => void) => {
	// Only listen to scroll events on #main-content
	const mainContent = document.getElementById('main-content')
	if (!mainContent) return

	// Create a throttled version of the position update to prevent excessive calls
	const throttledPositionUpdate = throttle(onPositionUpdate, 200)

	// Create a ResizeObserver for the main content area (handles sidebar movement)
	const resizeObserver = new ResizeObserver(throttledPositionUpdate)

	// Observe main content for resize events (handles sidebar movement)
	resizeObserver.observe(mainContent)

	// Add scroll listener only to main content
	mainContent.addEventListener('scroll', throttledPositionUpdate, true)

	return () => {
		resizeObserver.disconnect()
		mainContent.removeEventListener('scroll', throttledPositionUpdate, true)
		throttledPositionUpdate.cancel()
	}
}

export const getSpotlightCookie = (): Record<string, boolean> => {
	try {
		const cookie = getCookie(SPOTLIGHT_CONSTANTS.COOKIE_NAME)
		return cookie ? JSON.parse(cookie as string) : {}
	} catch {
		return {}
	}
}

export const setSpotlightCookie = (name: string, value: boolean): void => {
	const cookie = getSpotlightCookie()
	setCookie(
		SPOTLIGHT_CONSTANTS.COOKIE_NAME,
		JSON.stringify({ ...cookie, [name]: value })
	)
}

export const hasSeenSpotlight = (name: string): boolean => {
	const cookie = getSpotlightCookie()
	return Boolean(cookie[name])
}

export const toggleSpotlight = (
	show: boolean,
	spotlightRef: React.RefObject<HTMLDivElement>,
	describes: string,
	spotlightName: string,
	pulseClassName: string
): void => {
	const target = document.querySelector(describes) as HTMLElement | null
	const spotlight = spotlightRef.current

	if (show) {
		try {
			spotlight?.showPopover()

			if (target) {
				target.classList.add(pulseClassName)
			}

			setSpotlightCookie(spotlightName, true)
			sendAnalytics({
				noun: 'Spotlight',
				pastTenseVerb: 'Viewed',
				eventParameters: { Name: spotlightName },
			})
		} catch (error) {
			console.error('Error showing spotlight:', error)
		}
	} else {
		if (target) {
			target.classList.remove(pulseClassName)
		}

		spotlight?.hidePopover()
		sendAnalytics({
			noun: 'Spotlight Close',
			pastTenseVerb: 'Clicked',
			eventParameters: { Name: spotlightName },
		})
	}
}
